import { Updater } from "use-immer";
import Slider from "react-slick";
import { memo, useCallback, useState } from "react";
import axios from "axios";
import JSZip from "jszip"; // Add this import
import "./HomePage.scss";
import ExportSummary from "../../../component/export-summary/ExportSummary";
import FilterRow from "../../../component/filter-row/FilterRow";
import { changeFilterLeaderboardAction } from "../../../service/utils/functions";
import {
  CustomerDataEntry,
  GenericGraphDataEntry,
} from "../../../service/utils/interfaces";
import EnvironmentRow from "./rows/environment-row/EnvironmentRow";
import SummaryRow from "./rows/summary-row/SummaryRow";
import SupportRow from "./rows/support-row/SupportRow";
import { WasteTotalVerticalEntry } from "../../../service/utils/interfaces";
import { SortOrder } from "../../../service/utils/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faFilePdf,
    faFileWord,
    faFileExcel,
    faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from 'react-tooltip';
import { 
  validateDownloadCompleteness, 
  createDownloadConfig, 
  DownloadProgress
} from '../../../utils/fileDownloadValidator';
import { trackDownload } from '../../../service/matomo';

const templateMap: Record<string, string> = {
  pdf:  `${process.env.REACT_APP_CARBONE_REPORT_HOME_TEMPLATE_PDF}`,
  docx: `${process.env.REACT_APP_CARBONE_REPORT_HOME_TEMPLATE_DOCX}`,
  pptx: `${process.env.REACT_APP_CARBONE_REPORT_HOME_TEMPLATE_PPTX}`,
  xlsx: `${process.env.REACT_APP_CARBONE_REPORT_HOME_TEMPLATE_XLSX}`,
};

// Helper to format date to ddmmyyyy
const formatDate = (date: Date | null): string => {
  if (!date) return "unknown";
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear().toString();
  return `${day}${month}${year}`;
};

const fileInfoMap: Record<string, { filename: string; contentType: string }> = {
  pdf: {
    filename: "BradHomepageReport.pdf",
    contentType: "application/pdf",
  },
  docx: {
    filename: "BradHomepageReport.docx",
    contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  pptx: {
    filename: "BradHomepageReport.pptx",
    contentType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  xlsx: {
    filename: "BradHomepageReport.xlsx",
    contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
};

interface HomePageUIProps {
  isLoading: boolean;
  customerList: CustomerDataEntry[];
  isTippingUser: boolean;
  leaderboardRecycling: {top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]};
  leaderboardEmissions: {top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]};
  leaderboardWaste: {top: WasteTotalVerticalEntry[], bottom: WasteTotalVerticalEntry[]};
  sitesPerCustomer: any[];
  wasteTypesPerCustomer: any[];
  startDate: Date | null;
  endDate: Date | null;
  exportInProgress: boolean;
  achievementDataArray: any[];
  totalWasteCount: number;
  recycledWasteTypes: any[];
  recycledWasteDetails: any[][];
  colorArray: any[];
  overallRecycledWastes: any[];
  overallRecycledWastesColors: any[];
  emissionsGeneratedDetails: GenericGraphDataEntry[];
  filterLeaderboardValue: number;
  sortingLeaderboardValue: SortOrder;
  suggestionsArray: any[];
  currentPage: number;
  supportUsername: string;
  supportEmail: string;
  eventbriteBanner: string;
  eventbriteTitle: string;
  eventbriteEventLink: string;
  selectedSites?: any;
  selectedWasteTypes?: any;
  selectedCustomer?: any;
  printRecycledWasteTypes: GenericGraphDataEntry[];
  printRecycledWasteDetails: any[][];
  WTByPeriodGraph: any[];
  WTByPeriodTable: any[];
  WTByPeriodLegend: any[];
  WTBySiteGraph: any[];
  WTBySiteTable: any[];
  WTBySiteLegend: any[];
  overallRecycledWastesExportTable: any[];
  LBRecyclingRateTopGraph: any[];
  LBRecyclingRateTopTable: any[];
  LBEmissionRateBottomGraph: any[];
  LBEmissionRateBottomTable: any[];
  LBReducedWasteRateTopGraph: any[];
  LBReducedWasteRateTopTable: any[];
  achievementsTipping: any[];
  levelDataTable: any[];
  feeDataTable: any[];
  tonnageDataTable: any[];

  containerRef: React.RefObject<HTMLDivElement>;
  sliderRef: React.RefObject<Slider | null>;

  setFilterLeaderboardValue: React.Dispatch<React.SetStateAction<number>>;
  setSortingLeaderboardValue: React.Dispatch<React.SetStateAction<number>>;
  setSelectedCustomer: React.Dispatch<React.SetStateAction<any>>;
  setSelectedSites: React.Dispatch<React.SetStateAction<any[]>>;
  setSelectedWasteTypes: React.Dispatch<React.SetStateAction<any[]>>;
  setDateRange: React.Dispatch<
    React.SetStateAction<[Date | null, Date | null]>
  >;
  setExportInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;

  darkMode: boolean;
  width: number;
  animationDuration: number;
  getCarboneExportPayload: () => any;
}

const HomePageUI = memo((props: HomePageUIProps) => {
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);

  const handleChangeCustomerData = useCallback(
    (val: any) => {
      props.setSelectedCustomer(val);
    },
    [props]
  );
  const handleChangeSiteData = useCallback(
    (val: any) => {
      props.setSelectedSites([...val]);
    },
    [props]
  );
  const handleChangeWasteTypeData = useCallback(
    (val: any) => {
      props.setSelectedWasteTypes([...val]);
    },
    [props]
  );
  const handleChangeTimeRange = useCallback(
    (update: [Date | null, Date | null]) => {
      props.setDateRange(update);
    },
    [props]
  );

  // Add SVG to PNG conversion function
  const convertSvgToPng = async (svgContent: string): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      img.onload = () => {
        canvas.width = img.naturalWidth || 800;
        canvas.height = img.naturalHeight || 600;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as ArrayBuffer);
            reader.onerror = () => reject(new Error('Failed to read PNG blob'));
            reader.readAsArrayBuffer(blob);
          } else {
            reject(new Error('Failed to create PNG blob'));
          }
        }, 'image/png', 0.95);
      };
      
      img.onerror = () => reject(new Error('Failed to load SVG'));
      
      if (!svgContent.includes('xmlns="http://www.w3.org/2000/svg"')) {
        svgContent = svgContent.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      
      const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      img.src = URL.createObjectURL(svgBlob);
    });
  };

  // Enhanced Excel processing function with comprehensive XML updates
  const processExcelFile = async (fileBuffer: ArrayBuffer): Promise<ArrayBuffer> => {
    const zip = await JSZip.loadAsync(fileBuffer);
    
    // Find SVG files in xl/media
    const svgFiles: { path: string; file: JSZip.JSZipObject; filename: string }[] = [];
    zip.forEach((relativePath, file) => {
      if (relativePath.startsWith('xl/media/') && relativePath.toLowerCase().endsWith('.svg')) {
        const filename = relativePath.split('/').pop() || '';
        svgFiles.push({ path: relativePath, file, filename });
      }
    });
    
    if (svgFiles.length === 0) {
      return fileBuffer;
    }
    
    console.log(`[Azure SWA] Found ${svgFiles.length} SVG files to convert`);
    
    // Create mapping for filename replacements
    const filenameMap = new Map<string, string>();
    
    // Convert SVG files to PNG
    for (const svgFile of svgFiles) {
      const svgContent = await svgFile.file.async('text');
      const pngBuffer = await convertSvgToPng(svgContent);
      const pngPath = svgFile.path.replace(/\.svg$/i, '.png');
      const pngFilename = svgFile.filename.replace(/\.svg$/i, '.png');
      
      // Store mapping for later reference updates
      filenameMap.set(svgFile.filename, pngFilename);
      
      // Add PNG file and remove SVG file
      zip.file(pngPath, pngBuffer);
      zip.remove(svgFile.path);
      
      console.log(`[Azure SWA] Converted ${svgFile.filename} to ${pngFilename}`);
    }
    
    // 1. Update [Content_Types].xml to register PNG MIME type
    const contentTypesFile = zip.file('[Content_Types].xml');
    if (contentTypesFile) {
      let contentTypesXml = await contentTypesFile.async('text');
      
      // Add PNG extension if not present
      if (!contentTypesXml.includes('Extension="png"')) {
        const pngExtension = '<Default Extension="png" ContentType="image/png"/>';
        contentTypesXml = contentTypesXml.replace('</Types>', `  ${pngExtension}\n</Types>`);
        zip.file('[Content_Types].xml', contentTypesXml);
        console.log('[Azure SWA] Updated Content_Types.xml with PNG support');
      }
    }
    
    // 2. Update all relationship files (fixed async issue)
    const relationshipUpdates: Promise<void>[] = [];
    
    zip.forEach((relativePath, file) => {
      if (relativePath.includes('_rels/') && relativePath.endsWith('.xml.rels')) {
        const updatePromise = (async () => {
          try {
            let content = await file.async('text');
            let updated = false;
            
            // Replace SVG references with PNG references
            filenameMap.forEach((pngFilename, svgFilename) => {
              const svgPattern = new RegExp(svgFilename.replace('.', '\\.'), 'g');
              if (content.includes(svgFilename)) {
                content = content.replace(svgPattern, pngFilename);
                updated = true;
              }
            });
            
            if (updated) {
              zip.file(relativePath, content);
              console.log(`[Azure SWA] Updated relationship file: ${relativePath}`);
            }
          } catch (error) {
            console.warn(`[Azure SWA] Failed to update ${relativePath}:`, error);
          }
        })();
        
        relationshipUpdates.push(updatePromise);
      }
    });
    
    // Wait for all relationship updates to complete
    await Promise.all(relationshipUpdates);
    
    // 3. Update drawing XML files
    const drawingUpdates: Promise<void>[] = [];
    
    zip.forEach((relativePath, file) => {
      if (relativePath.startsWith('xl/drawings/') && relativePath.endsWith('.xml') && !relativePath.includes('_rels')) {
        const updatePromise = (async () => {
          try {
            let content = await file.async('text');
            let updated = false;
            
            // Replace SVG references with PNG references in drawing XML
            filenameMap.forEach((pngFilename, svgFilename) => {
              if (content.includes(svgFilename)) {
                const svgPattern = new RegExp(svgFilename.replace('.', '\\.'), 'g');
                content = content.replace(svgPattern, pngFilename);
                updated = true;
              }
            });
            
            if (updated) {
              zip.file(relativePath, content);
              console.log(`[Azure SWA] Updated drawing file: ${relativePath}`);
            }
          } catch (error) {
            console.warn(`[Azure SWA] Failed to update ${relativePath}:`, error);
          }
        })();
        
        drawingUpdates.push(updatePromise);
      }
    });
    
    // Wait for all drawing updates to complete
    await Promise.all(drawingUpdates);
    
    // 4. Update worksheet relationship files
    const worksheetUpdates: Promise<void>[] = [];
    
    zip.forEach((relativePath, file) => {
      if (relativePath.startsWith('xl/worksheets/_rels/') && relativePath.endsWith('.xml.rels')) {
        const updatePromise = (async () => {
          try {
            let content = await file.async('text');
            let updated = false;
            
            // Replace SVG references with PNG references
            filenameMap.forEach((pngFilename, svgFilename) => {
              if (content.includes(svgFilename)) {
                const svgPattern = new RegExp(svgFilename.replace('.', '\\.'), 'g');
                content = content.replace(svgPattern, pngFilename);
                updated = true;
              }
            });
            
            if (updated) {
              zip.file(relativePath, content);
              console.log(`[Azure SWA] Updated worksheet relationship: ${relativePath}`);
            }
          } catch (error) {
            console.warn(`[Azure SWA] Failed to update ${relativePath}:`, error);
          }
        })();
        
        worksheetUpdates.push(updatePromise);
      }
    });
    
    // Wait for all worksheet updates to complete
    await Promise.all(worksheetUpdates);
    
    // 5. Update any chart XML files that might reference the media
    const chartUpdates: Promise<void>[] = [];
    
    zip.forEach((relativePath, file) => {
      if (relativePath.startsWith('xl/charts/') && relativePath.endsWith('.xml')) {
        const updatePromise = (async () => {
          try {
            let content = await file.async('text');
            let updated = false;
            
            // Replace SVG references with PNG references in chart XML
            filenameMap.forEach((pngFilename, svgFilename) => {
              if (content.includes(svgFilename)) {
                const svgPattern = new RegExp(svgFilename.replace('.', '\\.'), 'g');
                content = content.replace(svgPattern, pngFilename);
                updated = true;
              }
            });
            
            if (updated) {
              zip.file(relativePath, content);
              console.log(`[Azure SWA] Updated chart file: ${relativePath}`);
            }
          } catch (error) {
            console.warn(`[Azure SWA] Failed to update ${relativePath}:`, error);
          }
        })();
        
        chartUpdates.push(updatePromise);
      }
    });
    
    // Wait for all chart updates to complete
    await Promise.all(chartUpdates);
    
    console.log('[Azure SWA] All XML files updated, generating final Excel file...');
    
    // Generate the modified Excel file with proper compression settings
    return await zip.generateAsync({ 
      type: 'arraybuffer',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });
  };

    // New Carbone Export Handler (uses data passed from HomePage)
    const handleExportWithCarbone = useCallback(
    async (format: "pdf" | "docx" | "xlsx") => {
        try {
        props.setExportInProgress(true);
        setDownloadProgress(null);
      
        const payload = props.getCarboneExportPayload();
        //   const payloadString = JSON.stringify(payload);
        //   console.log("[Carbone Export] Payload client number:", payloadString); 
        const fileInfo = fileInfoMap[format];


            // Build the suffix string
            const selectedCustomerStr = String(props.selectedCustomer ?? "unknown");
            const startDateStr = formatDate(props.startDate);
            const endDateStr = formatDate(props.endDate);

            // Insert the concatenated string before file extension in filename
            const baseName = "BradHomepageReport";
            const match = String(props.selectedCustomer.label).match(/\((\d+)\)[^()]*$/);
            const extractedCustomerID = match ? match[1] : "";
            const suffix = `_${extractedCustomerID}_${startDateStr}_${endDateStr}`;

            if (format === "pdf") {
            console.log("Generating PDF with suffix:", suffix);
            fileInfo.filename = `${baseName}${suffix}.pdf`;
            } else if (format === "docx") {
            console.log("Generating Word with suffix:", suffix);
            fileInfo.filename = `${baseName}${suffix}.docx`;
            } else if (format === "xlsx") {
            console.log("Generating Excel with suffix:", suffix);
            fileInfo.filename = `${baseName}${suffix}.xlsx`;
            }

        const templateId = templateMap[format];

      // Enhanced download configuration with progress tracking
      const downloadConfig = createDownloadConfig(
        (progress) => {
          setDownloadProgress(progress);
          console.log(`Download progress: ${progress.percentage}%`);
        },
        300000, // 5 minute timeout
        'arraybuffer' // Change to arraybuffer for Excel processing
      );

      console.log('[Export] Starting download...');
        const response = await axios.post(
            `${process.env.REACT_APP_CARBONE_RENDER_ENDPOINT}/render/${templateId}?download=true`,
            {
                data: payload,
                convertTo: format,
            },
            {
            headers: {
                "Content-Type": "application/json",
                "carbone-version": "5",
                Authorization: `Bearer ${process.env.REACT_APP_CARBONE_API_KEY}`,
            },
          ...downloadConfig
            }
        );

      if (response.status === 200) {
        console.log('[Export] Download completed, validating file...');
        
        // Extract content length from response headers
        const contentLength = response.headers['content-length'] 
          ? parseInt(response.headers['content-length'], 10) 
          : undefined;

        // Validate file completeness
        const validation = await validateDownloadCompleteness(
          response.data,
          contentLength,
          fileInfo.contentType
        );

        console.log('[Export] Validation result:', validation);

        // Handle validation results
        if (!validation.isValid) {
          console.error('[Export] File validation failed:', validation.errors);
          alert(`Download failed: ${validation.errors.join(', ')}`);
          return;
        }

        // Log warnings if any
        if (validation.warnings.length > 0) {
          console.warn('[Export] Validation warnings:', validation.warnings);
        }

        let finalData = response.data;
        
        // Process Excel files to convert SVG to PNG
        if (format === "xlsx") {
          try {
            finalData = await processExcelFile(response.data);
            console.log('[Export] Excel file processed for SVG to PNG conversion');
          } catch (error) {
            console.warn('[Export] SVG conversion failed, using original file:', error);
          }
        }

        // Create blob with processed data
        const blob = new Blob([finalData], { type: fileInfo.contentType });

        console.log(`[Export] File validated successfully. Size: ${blob.size} bytes`);

        // Download the validated file
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileInfo.filename;
            // Stops Matomo's automatic link tracking recording the blob: URL; tracked explicitly below
            link.className = "matomo_ignore";
            document.body.appendChild(link);
            link.click();
            trackDownload(fileInfo.filename, "carbone-report");

        // Clean up with slight delay to ensure download started
        setTimeout(() => {
          document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
        }, 100);
        
        console.log('[Export] Download initiated successfully');
        
        // Show success message with file details
        if (validation.warnings.length > 0) {
          alert(`File downloaded successfully with warnings:\n${validation.warnings.join('\n')}`);
        } else {
          console.log(`File downloaded successfully: ${fileInfo.filename} (${blob.size} bytes)`);
        }
        
      } else {
        console.error(`[Export] Unexpected response status: ${response.status}`);
        alert(`Export failed with status: ${response.status}`);
      }
      
    } catch (err: any) {
      console.error("[Export Error]", err);
      
      // Enhanced error handling
      let errorMessage = "Export failed. Try again later.";
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = "Download timeout. The file may be too large or the connection is slow.";
      } else if (err.response?.status === 413) {
        errorMessage = "File too large to download.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error during file generation.";
      } else if (err.response?.status) {
        errorMessage = `Server error: ${err.response.status} - ${err.response.statusText}`;
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      alert(errorMessage);
      
        } finally {
        props.setExportInProgress(false);
      setDownloadProgress(null);
        }
    },
    [props]
  );

  return (
    <div
      ref={props.containerRef}
      className={"home-wrapper" + (props.darkMode ? " dark" : "")}
    >
      {!props.isLoading && (
        <>
          <div className="filter-row row">
            <FilterRow
              width={props.width}
              customerData={props.customerList}
              isTippingUser={props.isTippingUser}
              onChangeCustomerData={handleChangeCustomerData}
              siteData={props.sitesPerCustomer}
              onChangeSiteData={handleChangeSiteData}
              wasteTypeData={props.wasteTypesPerCustomer}
              onChangeWasteTypeData={handleChangeWasteTypeData}
              displayDateRange
              startDate={props.startDate}
              endDate={props.endDate}
              onChangeTimeRange={handleChangeTimeRange}
              darkMode={props.darkMode}
            />
            {!props.isTippingUser && (
                <div
                    className="export-controls"
                    style={{ display: "flex", gap: 12, alignItems: "center" }}
                    >
                    {(() => {

                        return (
                        <>
                            {/* PDF Button */}
                            <button
                            className="download-report-button orange-btn"
                            title="Download PDF"
                            data-tooltip-id="pdf-tooltip"
                            data-tooltip-content={"Download PDF"}
                            onClick={async () => {
                                await handleExportWithCarbone("pdf");
                            }}
                            style={{
                                width: 40,
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            >
                            <FontAwesomeIcon
                                icon={props.exportInProgress ? faSpinner : faFilePdf}
                                style={{ fontSize: 22 }}
                            />
                            </button>
                            <Tooltip id="pdf-tooltip" />

                            {/* Excel Button */}
                            <button
                            className="download-report-button orange-btn"
                            title="Download Excel"
                            data-tooltip-id="excel-tooltip"
                            data-tooltip-content={"Download Excel"}
                            onClick={async () => {
                                await handleExportWithCarbone("xlsx");
                            }}
                            disabled={props.exportInProgress}
                            style={{
                                width: 40,
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            >
                            <FontAwesomeIcon
                                icon={props.exportInProgress ? faSpinner : faFileExcel}
                                style={{ fontSize: 22 }}
                            />
                            </button>
                            <Tooltip id="excel-tooltip" />
                        </>
                        );
                    })()}
                    </div>

            )}
          </div>
          <SummaryRow
            isTippingUser={props.isTippingUser}
            achievementDataArray={props.achievementDataArray}
            achievementDataArrayTipping={props.achievementsTipping}
            totalWasteCount={props.totalWasteCount}
            recycledWasteTypes={props.recycledWasteTypes}
            recycledWasteDetails={props.recycledWasteDetails}
            colorArray={props.colorArray}
            width={props.width}
            darkMode={props.darkMode}
            animationDuration={props.animationDuration}
          />
          <EnvironmentRow
            isTippingUser={props.isTippingUser}
            overallRecycledWastes={props.overallRecycledWastes}
            overallRecycledWastesColors={props.overallRecycledWastesColors}
            emissionsGeneratedDetails={props.emissionsGeneratedDetails}
            filterLeaderboardValue={props.filterLeaderboardValue}
            sortingLeaderboardValue={props.sortingLeaderboardValue}
            leaderboardRecycling={props.leaderboardRecycling}
            leaderboardEmissions={props.leaderboardEmissions}
            leaderboardWaste={props.leaderboardWaste}
            levelDataTable={props.levelDataTable}
            feeDataTable={props.feeDataTable}
            tonnageDataTable={props.tonnageDataTable}
            setSortingLeaderboardValue={props.setSortingLeaderboardValue}
            setFilterLeaderboardValue={props.setFilterLeaderboardValue}
            darkMode={props.darkMode}
            width={props.width}
          />
          <SupportRow
            suggestionsArray={props.suggestionsArray}
            currentPage={props.currentPage}
            supportUsername={props.supportUsername}
            supportEmail={props.supportEmail}
            eventbriteBanner={props.eventbriteBanner}
            eventbriteTitle={props.eventbriteTitle}
            eventbriteEventLink={props.eventbriteEventLink}
            sliderRef={props.sliderRef}
            setCurrentPage={props.setCurrentPage}
            width={props.width}
            darkMode={props.darkMode}
          />
          <ExportSummary
            startDate={props.startDate}
            endDate={props.endDate}
            selectedSites={props.selectedSites}
            selectedWasteTypes={props.selectedWasteTypes}
            selectedCustomer={props.selectedCustomer}
            achievementDataArray={props.achievementDataArray}
            printRecycledWasteTypes={props.printRecycledWasteTypes}
            printRecycledWasteDetails={props.printRecycledWasteDetails}
            WTByPeriodGraph={props.WTByPeriodGraph}
            WTByPeriodTable={props.WTByPeriodTable}
            WTByPeriodLegend={props.WTByPeriodLegend}
            WTBySiteGraph={props.WTBySiteGraph}
            WTBySiteTable={props.WTBySiteTable}
            WTBySiteLegend={props.WTBySiteLegend}
            overallRecycledWastes={props.overallRecycledWastes}
            overallRecycledWastesExportTable={
              props.overallRecycledWastesExportTable
            }
            overallRecycledWastesColors={props.overallRecycledWastesColors}
            emissionsGeneratedDetails={props.emissionsGeneratedDetails}
            LBRecyclingRateTopGraph={props.LBRecyclingRateTopGraph}
            LBRecyclingRateTopTable={props.LBRecyclingRateTopTable}
            LBEmissionRateBottomGraph={props.LBEmissionRateBottomGraph}
            LBEmissionRateBottomTable={props.LBEmissionRateBottomTable}
            LBReducedWasteRateTopGraph={props.LBReducedWasteRateTopGraph}
            LBReducedWasteRateTopTable={props.LBReducedWasteRateTopTable}
          />
        </>
      )}
    </div>
  );
});

export default HomePageUI;
